// Vendor imports
const Immutable = require('immutable')
// Shared imports
const { TYPES: modelTypes } = require('shared/model-types')
const articleUtils = require('shared/model-helpers/article')
// Common Client V2 imports
const { FeedStreamMetaRecord } = require('client-v2/common/containers/feed/records')
const { wrapFeedStream } = require('client-v2/common/containers/feed/wrappers')
const { listReviver } = require('client-v2/common/state/record-reviver')
const PageHistoryRecord = require('client-v2/common/state/router/record/page-history')
const {
  FetchStateEnum,
  NextPageStateEnum
} = require('client-v2/common/utils/enums')
// Container imports
const ArticleProfileSlideRecord = require('./slide-record')

const PAGE_TITLE_PREFIX = 'Mix · '

class ArticleProfileModalRecord extends Immutable.Record({
  currentIndex: Number(),
  callerPageHistory: null, // PageHistoryRecord
  depth: Number(),
  nextPageCallback: null, // Function (NON-SERIALIZABLE!)
  nextPageState: NextPageStateEnum.UNAVAILABLE,
  slides: Immutable.List(),
  streamMeta: new FeedStreamMetaRecord()
}) {

  // Initialization helpers

  setup(options) {
    return this.merge({ depth: this.depth + 1 })
    .setupCallerPageHistory(options)
    .setupPagination(options)
    .setupSlides(options)
  }

  setupCallerPageHistory() {
    // Maintain the original callerPageHistory with nested 'show' actions
    if (this.callerPageHistory) {
      return this
    }
    return this.merge({
      callerPageHistory: PageHistoryRecord.fromCurrentState()
    })
  }

  setupPagination(options) {
    const { nextPageCallback } = options
    const hasCallback = typeof nextPageCallback === 'function'
    return this.merge({
      nextPageCallback,
      nextPageState: NextPageStateEnum.fromBoolean(hasCallback)
    })
  }

  setupSlides(options) {
    const { initialIndex, items, meta } = options
    // Handle "incomplete" articles from wiggin `urlDataBody`
    const articleState = options.articlesNeedFetch
    ? FetchStateEnum.EMPTY
    : FetchStateEnum.LOADED
    // Create article-profile slide-records from articles
    const normalizeProps = { articleState }
    const slideRecords = normalizeSlides(items, normalizeProps)
    // Clamp currentIndex within the bounds of slideRecords
    const maxIndex = slideRecords.size - 1
    const currentIndex = Math.max(0, Math.min(maxIndex, initialIndex))
    return this.merge({
      currentIndex,
      slides: slideRecords,
      streamMeta: new FeedStreamMetaRecord(meta)
    })
  }

  // Getters

  getCurrentSlide() {
    return this.slides.get(this.currentIndex)
  }

  getCurrentSlidePageContext() {
    return this.streamMeta.getPageContext(modelTypes.ARTICLE)
    .merge({
      pageName: 'article.card',
      recId: this.getCurrentSlide().articleMeta.recId
    })
  }

  getCurrentSlidePageHistory() {
    const { article: { hash, title } } = this.getCurrentSlide()
    const pathname = articleUtils.prettyPath(hash, title)
    return PageHistoryRecord.newState({
      location: { pathname },
      title: PAGE_TITLE_PREFIX + title,
      pageContext: this.getCurrentSlidePageContext()
    })
  }

  findSlideIndexesForUrlIds(urlIds) {
    return this.slides.reduce((acc, slide, index) => {
      if (urlIds.includes(slide.article['url_id'])) {
        acc.push(index)
      }
      return acc
    }, [])
  }

  findSlideIndexesForHostname(hostname) {
    return this.slides.reduce((acc, slide, index) => {
      if (slide.article.hostname === hostname) {
        acc.push(index)
      }
      return acc
    }, [])
  }

  // Setters

  removeSlidesByUrlIds(urlIds) {
    const slideIndexes = this.findSlideIndexesForUrlIds(urlIds)
    return this.removeSlidesAtIndexes(slideIndexes)
  }

  removeSlidesByHostname(hostname) {
    if (!this.streamMeta.recBatchId && !this.streamMeta.urlRecBatchId) {
      // Only apply domain blocks to recommended feeds
      return this
    }
    const slideIndexes = this.findSlideIndexesForHostname(hostname)
    return this.removeSlidesAtIndexes(slideIndexes)
  }

  removeSlidesAtIndexes(indexes) {
    // Find the next slide that is not going to be removed
    let nextCurrentIndex = this.currentIndex
    while (indexes.includes(nextCurrentIndex)) { nextCurrentIndex += 1 }
    const nextCurrentSlide = this.slides.get(nextCurrentIndex)
    // Remove the slides
    const nextState = this.update('slides', slides => {
      return slides.filter((slide, index) => !indexes.includes(index))
    })
    // Update the current index to the next current slide
    const nextCurrentSlideUpdatedIndex = nextState.slides.indexOf(nextCurrentSlide)
    return nextState.setCurrentIndex(nextCurrentSlideUpdatedIndex)
  }

  setCurrentIndex(index) {
    const maxIndex = this.slides.size - 1
    const clampedIndex = Math.max(Math.min(index, maxIndex), 0)
    return this.merge({
      currentIndex: clampedIndex
    })
  }

  setNextPageLoaded(feedStream) {
    const { items, meta } = wrapFeedStream(feedStream)
    const nextPageAvailable = feedStream.nextPageAvailable || meta.nextUrl
    return this.mergeDeep({
      nextPageState: NextPageStateEnum.fromBoolean(nextPageAvailable),
      slides: normalizeSlides(items),
      streamMeta: meta
    })
  }

  setNextPageLoading() {
    return this.merge({
      nextPageState: NextPageStateEnum.LOADING
    })
  }

  // Serialization/deserialization helpers

  toJS() {
    // This record is NON-SERIALIZABLE!
    return null
  }

  static reviver() {
    // This record is NON-SERIALIZABLE!
    return null
  }

}

// Record revivers
const slideReviver = ArticleProfileSlideRecord.reviver
const slidesReviver = listReviver(slideReviver)

function normalizeSlides(feedItems, slideProps = {}) {
  // Assume that the articles are fully-loaded unless otherwise-specified
  slideProps.articleState = slideProps.articleState || FetchStateEnum.LOADED
  // fromJS will pass Immutable.List through untouched, so it needs special revival
  if (Immutable.List.isList(feedItems)) {
    // Convert Immutable.List<FeedItemRecord> into Immutable.List<ArticleProfileSlideRecord>
    return feedItems.map(feedItem => Immutable.fromJS(
      convertFeedItemToArticleProfileSlide(feedItem),
      slideReviver
    ))
  }
  // Convert Array<FeedItemRecord> into Immutable.List<ArticleProfileSlideRecord>
  return Immutable.fromJS(
    feedItems.map(convertFeedItemToArticleProfileSlide),
    slidesReviver
  )
  function convertFeedItemToArticleProfileSlide(feedItem) {
    const articleProfileSlide = {
      article: feedItem.article,
      articleMeta: feedItem.meta
    }
    return Object.assign(articleProfileSlide, slideProps)
  }
}

module.exports = Object.assign(ArticleProfileModalRecord, {
  // Record revivers
  slidesReviver,
  normalizeSlides
})
